import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  MagnifyingGlassIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import normatividadService from '../../../services/normatividadService'
import { TemaNormativo } from '../../../types/normatividad'
import { usePermisos } from '../../../ganchos/usePermisos'

const PaginaGestionNormativa: React.FC = () => {
  const { tienePermiso } = usePermisos()
  const [normas, setNormas] = useState<TemaNormativo[]>([])
  const [filtro, setFiltro] = useState('')
  const [cargando, setCargando] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 15
  // const fileInputRef = useRef<HTMLInputElement>(null)

  const cargarNormas = async () => {
    setCargando(true)
    try {
      const data = await normatividadService.getAllNormas()
      setNormas(data)
    } catch (error) {
      console.error("Error cargando normas", error)
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => {
    cargarNormas()
    
    const handleUpdate = () => cargarNormas()
    window.addEventListener('normatividad_updated', handleUpdate)
    return () => window.removeEventListener('normatividad_updated', handleUpdate)
  }, [])

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de eliminar esta normativa?')) {
      try {
        await normatividadService.deleteNorma(id)
        cargarNormas()
      } catch (error) {
        console.error(error)
        alert("Error al eliminar la normativa")
      }
    }
  }

  /* 
  // Funcionalidad de Importar/Exportar deshabilitada al mover a Base de Datos
  const handleExport = () => { ... }
  const handleImportClick = () => { ... }
  const handleFileChange = ...
  */

  const normasFiltradas = normas.filter(n => 
    n.titulo.toLowerCase().includes(filtro.toLowerCase()) ||
    n.descripcion.toLowerCase().includes(filtro.toLowerCase()) ||
    n.tipo.toLowerCase().includes(filtro.toLowerCase())
  )

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = normasFiltradas.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(normasFiltradas.length / itemsPerPage)

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber)

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestión de Normatividad</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Administra leyes, reglamentos, tratados y documentos legales del sistema.
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* 
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            className="hidden" 
            accept=".json" 
          />
          */}
          
          {/* Funcionalidad Importar/Exportar desactivada
          {tienePermiso('normativa:crear') && (
            <button
              onClick={handleImportClick}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none transition-colors"
            >
              <ArrowUpTrayIcon className="-ml-1 mr-2 h-5 w-5" />
              Importar
            </button>
          )}

          {tienePermiso('normativa:exportar') && (
            <button
              onClick={handleExport}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none transition-colors"
            >
              <ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5" />
              Exportar
            </button>
          )}
          */}

          {tienePermiso('normativa:crear') && (
            <Link
              to="/admin/normatividad/nueva"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Nueva Normativa
            </Link>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Buscar por título, descripción o tipo..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
      </div>

      {/* Tabla de Resultados */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Documento
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Categoría
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Estado
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {cargando ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500">
                    Cargando normatividad...
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((norma) => (
                  <tr key={norma.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1" title={norma.titulo}>
                          {norma.titulo}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1" title={norma.descripcion}>
                          {norma.descripcion}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {norma.tipo}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 capitalize">
                      {norma.categoria.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {norma.actualizado}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/normatividad/visor/${norma.id}`}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          title="Ver Documento"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </Link>
                        {tienePermiso('normativa:editar') && (
                          <Link
                            to={`/admin/normatividad/editar/${norma.id}`}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                            title="Editar"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </Link>
                        )}
                        {tienePermiso('normativa:eliminar') && (
                          <button
                            onClick={() => handleDelete(norma.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                            title="Eliminar"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500">
                    No se encontraron resultados para "{filtro}".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-gray-700 dark:text-gray-300">
          Mostrando <span className="font-medium">{normasFiltradas.length > 0 ? indexOfFirstItem + 1 : 0}</span> a <span className="font-medium">{Math.min(indexOfLastItem, normasFiltradas.length)}</span> de <span className="font-medium">{normasFiltradas.length}</span> registros
        </div>

        {totalPages > 1 && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md border ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:border-gray-700'
                  : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
              }`}
            >
              Anterior
            </button>
            
            <div className="hidden sm:flex space-x-1">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => paginate(i + 1)}
                  className={`px-3 py-1 rounded-md border ${
                    currentPage === i + 1
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-md border ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:border-gray-700'
                  : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
              }`}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaginaGestionNormativa
